import { getPasswordManagerAttributes, Icon } from '@amelio/ui-library';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { iconBank } from '../../utils/iconBank';
import { getIconSvg } from '../../utils/iconSvgs';

interface IconPickerProps {
    selectedIcon: string | null;
    onIconChange: (iconId: string | null) => void;
}

export default function IconPicker({ selectedIcon, onIconChange }: IconPickerProps) {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');

    const filteredIcons = useMemo(() => {
        if (!searchQuery) return iconBank;
        const query = searchQuery.toLowerCase();
        return iconBank.filter(
            (icon) =>
                icon.name.toLowerCase().includes(query) ||
                icon.keywords.some((keyword) => keyword.toLowerCase().includes(query)),
        );
    }, [searchQuery]);

    return (
        <div className={'mb-2 flex flex-col gap-3'}>
            <label className={'text-navy text-body-sm font-medium'}>
                {t('visionValues.chooseIcon')}
            </label>

            <div className={'relative'}>
                <Icon
                    name={'search-search-normal-1'}
                    size={'sm'}
                    colorClassName={'text-gray-400'}
                    className={'pointer-events-none absolute top-1/2 left-3 -translate-y-1/2'}
                />

                <input
                    {...getPasswordManagerAttributes()}
                    type={'text'}
                    placeholder={t('visionValues.iconSearchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={
                        'text-navy focus-ring-primary text-body-sm h-10 w-full rounded-lg border border-gray-300 bg-white py-2 pr-3 pl-9 focus:ring-2 focus:outline-none'
                    }
                />
            </div>

            <div
                className={
                    'scrollbar-glass h-[200px] overflow-y-auto rounded-lg border border-gray-300 bg-white p-3 md:h-[220px]'
                }
            >
                <div
                    className={
                        'grid grid-cols-4 justify-items-center gap-2 sm:grid-cols-6 md:grid-cols-10'
                    }
                >
                    {filteredIcons.map((icon) => (
                        <button
                            key={icon.id}
                            type={'button'}
                            onClick={() => onIconChange(icon.id)}
                            className={`flex h-[50px] w-[50px] items-center justify-center rounded-lg text-white transition-all sm:h-[55px] sm:w-[55px] md:h-[60px] md:w-[60px] ${
                                selectedIcon === icon.id
                                    ? 'ring-primary scale-105 shadow-lg ring-2'
                                    : 'hover:scale-105 hover:shadow-md'
                            }`}
                            style={{ background: icon.bg }}
                            title={icon.name}
                            aria-label={icon.name}
                        >
                            {getIconSvg(icon.id, 28)}
                        </button>
                    ))}
                </div>

                {filteredIcons.length === 0 && (
                    <div
                        className={'flex h-full items-center justify-center text-sm text-gray-500'}
                    >
                        {t('visionValues.noIconFound', { query: searchQuery })}
                    </div>
                )}
            </div>

            <p className={'text-sm text-gray-400'}>
                {t('visionValues.iconsAvailable', { count: filteredIcons.length })}
            </p>
        </div>
    );
}
